/**
 * Phase 86 Plan 86-04 Task 3 — zone-banded list assembly over the rebuilt
 * `MyTaskRow` (`.planning/design/mytasks-compost/README.md`, "Zone header
 * bands" + "Interactions & behavior" sort-switch paragraph).
 *
 * In `rot` mode this mounts one `MtZoneBand` per NON-EMPTY zone (skipping a
 * zone with zero tasks entirely — README "Empty states"), each followed by
 * that zone's rows. Terminal tasks render with no band above them, matching
 * the design reference (`my-tasks-compost-5a.html`'s DONE row has no band).
 * In `pressure`/`due` mode the same rows render flat, no bands. In
 * `project` mode the existing sticky `MyTasksProjectGroup` header is
 * preserved exactly as before this plan.
 *
 * `graveyardTrailingSlot` is the slot contract 86-05 uses to mount the
 * BULK CLOSE chip: it is threaded straight into `MtZoneBand`'s own
 * `trailing` prop for the graveyard zone only.
 *
 * Phase 86 Plan 86-06 — this component now also owns:
 *   - `viewportMode` — forwarded to `MyTasksColumnLegend` (skipped entirely
 *     in `card` mode — a stacked card list has no shared column header) and
 *     to every rendered `MyTaskRow` / `MyTasksProjectGroup`, across every
 *     sort mode, so the responsive collapse behaves identically whichever
 *     way the list is currently grouped.
 *   - `cursorTaskId` — the keyboard cursor row (README "Keyboard"),
 *     forwarded the same way as `viewportMode`.
 *   - a `MyTaskRowHandle` registry (`rowHandles`), fed by every row's own
 *     `registerHandle` callback, exposed to the page via `onReady` as a
 *     `{ triggerTouch(taskId) }` API — this is what lets `MyTasksPage`'s
 *     `T`-shortcut handler reach the correct row's own TOUCH state without
 *     the list needing `forwardRef` (which would break
 *     `MyTasksZonedList.test.tsx`'s `Parameters<typeof MyTasksZonedList>`
 *     render helper, since a `ForwardRefExoticComponent` is not a callable
 *     function type).
 */
import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { MyTasksColumnLegend } from './MyTasksColumnLegend';
import { MtZoneBand } from './MtZoneBand';
import type { CompostBandZone } from './compostTokens';
import {
  MyTaskRow,
  type Density,
  type MyTaskRowAudience,
  type MyTaskRowHandle,
  type MyTasksViewportMode,
} from './MyTaskRow';
import { MyTasksProjectGroup, type ProjectGroupData } from './MyTasksProjectGroup';
import type { MyTaskDto } from '../../services/api/tasks';
import type { CompostSortMode, CompostZoneGroup } from '../../utils/myTasksCompost';

/** The imperative API `MyTasksPage` gets via `onReady` — lets the `T`
 * keyboard shortcut trigger TOUCH on whichever row is currently the cursor,
 * regardless of which sort mode / rendering branch that row is in. */
export interface MyTasksZonedListApi {
  triggerTouch: (taskId: number) => void;
}

export interface MyTasksZonedListProps {
  sortMode: CompostSortMode;
  /** Flat, already-sorted list — used directly in pressure/due mode. */
  tasks: MyTaskDto[];
  /** Rot-mode-only: non-empty zone groups, in canonical zone order. */
  zoneGroups: CompostZoneGroup[];
  /** Project-mode-only: the sticky-header project groups. */
  projectGroups: ProjectGroupData[];
  density: Density;
  audience?: MyTaskRowAudience;
  filterHint?: string | null;
  onClearFilters?: () => void;
  /** Slot contract for 86-05's BULK CLOSE chip — mounted on the graveyard band only. */
  graveyardTrailingSlot?: ReactNode;
  /** Rendered inside the same scroll container, after the last row — the
   * FREE TO GRAB band (+ the revealed claimable list) lives here so it
   * scrolls with the table rather than floating as a fixed footer. */
  trailing?: ReactNode;
  /** Phase 86-06 — which of the three responsive layouts every row + the
   * column legend should render. Defaults to `wide` (unchanged from before
   * this plan). */
  viewportMode?: MyTasksViewportMode;
  /** Phase 86-06 — the keyboard cursor's current task id, or null. */
  cursorTaskId?: number | null;
  /** Petra UAT 2026-08-18 — forwarded to every row so a click opens the ticket
   * over the list instead of navigating away from it. */
  onRowClick?: (taskId: number) => void;
  /** Phase 86-06 — called once with this list's imperative `triggerTouch`
   * API so `MyTasksPage` can reach the cursor row's TOUCH state. */
  onReady?: (api: MyTasksZonedListApi) => void;
}

export function MyTasksZonedList({
  sortMode,
  tasks,
  zoneGroups,
  projectGroups,
  density,
  audience = 'internal',
  filterHint,
  onClearFilters,
  graveyardTrailingSlot,
  trailing,
  viewportMode = 'wide',
  cursorTaskId = null,
  onRowClick,
  onReady,
}: MyTasksZonedListProps) {
  const isEmpty =
    sortMode === 'project' ? projectGroups.length === 0 : tasks.length === 0;

  // Phase 86-06 — one handle registry per mount, fed by every rendered
  // row's own `registerHandle` callback regardless of which branch below
  // rendered it (zone-banded, flat, or inside a MyTasksProjectGroup).
  const rowHandles = useRef(new Map<number, MyTaskRowHandle>());
  const registerRowHandle = useCallback((taskId: number, handle: MyTaskRowHandle | null) => {
    if (handle) rowHandles.current.set(taskId, handle);
    else rowHandles.current.delete(taskId);
  }, []);

  const api = useMemo<MyTasksZonedListApi>(
    () => ({
      triggerTouch: (taskId: number) => rowHandles.current.get(taskId)?.triggerTouch(),
    }),
    [],
  );

  useEffect(() => {
    onReady?.(api);
  }, [onReady, api]);

  return (
    <div className="flex-1 flex flex-col overflow-y-auto" data-testid="my-tasks-zoned-list">
      {viewportMode !== 'card' && <MyTasksColumnLegend viewportMode={viewportMode} />}

      {filterHint && (
        <div
          className="flex items-center gap-2 px-4 py-2 bg-ms-orange/5 border-b border-ms-orange/20 text-[11px] text-ms-text-secondary"
          data-testid="my-tasks-filter-hint"
        >
          <span>{filterHint}</span>
          {onClearFilters && (
            <button
              type="button"
              onClick={onClearFilters}
              className="ml-auto text-[11px] font-semibold text-ms-orange-dark hover:text-ms-orange"
            >
              Clear filters →
            </button>
          )}
        </div>
      )}

      {isEmpty && filterHint && (
        <div
          data-testid="my-tasks-empty-filter-result"
          className="flex flex-col items-center justify-center py-16 gap-2 text-[12px] text-ms-text-muted"
        >
          <span>No tasks match the current filters.</span>
          {onClearFilters && (
            <button
              type="button"
              onClick={onClearFilters}
              className="text-[11.5px] font-semibold text-ms-orange-dark hover:text-ms-orange"
            >
              Clear filters →
            </button>
          )}
        </div>
      )}

      {sortMode === 'project' &&
        projectGroups.map((group) => (
          <MyTasksProjectGroup
            key={group.projectId}
            group={group}
            density={density}
            viewportMode={viewportMode}
            cursorTaskId={cursorTaskId}
            onRowClick={onRowClick}
            registerHandle={registerRowHandle}
          />
        ))}

      {sortMode === 'rot' &&
        zoneGroups.map((group) => (
          <div key={group.zone}>
            {group.zone !== 'terminal' && (
              <MtZoneBand
                zone={group.zone as CompostBandZone}
                count={group.tasks.length}
                trailing={group.zone === 'graveyard' ? graveyardTrailingSlot : undefined}
              />
            )}
            {group.tasks.map((task, idx) => (
              <MyTaskRow
                key={task.taskId}
                task={task}
                density={density}
                rowIndex={idx}
                audience={audience}
                viewportMode={viewportMode}
                isCursor={cursorTaskId === task.taskId}
                onRowClick={onRowClick}
                registerHandle={registerRowHandle}
              />
            ))}
          </div>
        ))}

      {sortMode !== 'rot' &&
        sortMode !== 'project' &&
        tasks.map((task, idx) => (
          <MyTaskRow
            key={task.taskId}
            task={task}
            density={density}
            rowIndex={idx}
            audience={audience}
            viewportMode={viewportMode}
            isCursor={cursorTaskId === task.taskId}
            registerHandle={registerRowHandle}
          />
        ))}

      {trailing}
    </div>
  );
}
